
// Custom hook to handle auto-logout logic for inactivity, including cross-tab sync and modal management
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from "react-router-dom";
import { clearAllState, loginStatus, loginTime } from '../../ducks/redux/actions/userChoices';
import { getCustomerId } from '../../ducks/redux/actions/getAddress';


// LocalStorage keys for tracking activity and logout state
const AUTOLOGOUT_KEY = 'autologout_last_active';
const POPUP_SHOWN_KEY = 'autologout_popup_shown';
const LOGOUT_KEY = 'autologout_force_logout';
const SECOND_MS = 60 * 1000;
// Inactivity and timeout durations (ms and seconds)
// Read inactivity limit (minutes) from env, fallback to 25
const INACTIVITY_LIMIT = (Number(process.env.REACT_APP_FORCE_LOGOUT_LIMIT_MINUTES) || 25) * SECOND_MS; // ms
const POPUP_GRACE =(Number(process.env.REACT_APP_POPUP_GRACE_MINUTES) || 5) * 60; // 5 minutes (in seconds)
const FORCE_LOGOUT_LIMIT = INACTIVITY_LIMIT +  ((Number(process.env.REACT_APP_FORCE_LOGOUT_LIMIT_MINUTES) || 10) * SECOND_MS); // 35 minutes

/**
 * useAutoLogout custom hook
 * Handles user inactivity detection, auto-logout, and cross-tab sync for session security.
 * Returns modal state, countdown, and handlers for UI integration.
 */
export function useAutoLogout() {
    const dispatch = useDispatch();
    const history = useHistory();
    // Modal open state for inactivity warning
    const [modalIsOpen, setIsOpen] = useState(false);
    // Modal open state for signout confirmation
    const [showSignoutMessage, setShowSignoutMessage] = useState(false);
    // Countdown timer (seconds) for inactivity popup
    const [countdown, setCountdown] = useState(POPUP_GRACE);
    // Ref to store interval ID for timer
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    /**
     * Updates the last active timestamp in localStorage and clears popup/logout flags.
     * Called on user activity and when continuing session.
     */
    const updateLastActive = useCallback(() => {
        localStorage.setItem(AUTOLOGOUT_KEY, Date.now().toString());
        localStorage.removeItem(POPUP_SHOWN_KEY);
        localStorage.removeItem(LOGOUT_KEY);
    }, []);

    /**
     * Sets up event listeners for user activity (mouse, keyboard, touch).
     * Resets inactivity timer on any activity.
     */
    useEffect(() => {
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
        const handler = () => updateLastActive();
        events.forEach(e => window.addEventListener(e, handler));
        return () => events.forEach(e => window.removeEventListener(e, handler));
    }, [updateLastActive]);

    /**
     * Listens for localStorage changes from other tabs/windows to sync popup and logout state.
     */
    useEffect(() => {
        const onStorage = (e) => {
            // Show inactivity popup if another tab triggers it
            if (e.key === POPUP_SHOWN_KEY && e.newValue === '1') {
                setIsOpen(true);
                setCountdown(POPUP_GRACE);
            }
            // Force logout if another tab triggers it
            if (e.key === LOGOUT_KEY && e.newValue === '1') {
                doLogout();
            }
            // Hide popup if activity is detected in another tab
            if (e.key === AUTOLOGOUT_KEY) {
                setIsOpen(false);
                setCountdown(POPUP_GRACE);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    /**
     * Performs logout: clears Redux state, resets modals, and navigates to start page.
     */
    const doLogout = useCallback(() => {
        dispatch(loginTime(''));
        dispatch(loginStatus(false));
        dispatch(getCustomerId(''));
        dispatch(clearAllState({}));
        setIsOpen(false);
        setShowSignoutMessage(true);
        setCountdown(POPUP_GRACE);
        history.push("/startpage");
    }, [dispatch, history]);

    /**
     * Main timer effect: checks inactivity every second, shows popup, and triggers logout if needed.
     */
    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            const lastActive = parseInt(localStorage.getItem(AUTOLOGOUT_KEY) || '0', 10);
            const currentActiveLimit = Date.now() - lastActive;
            // If no activity, reset last active
            if (!lastActive || isNaN(lastActive)) {
                updateLastActive();
                return;
            }
            // Show inactivity popup if limit reached
            if (!modalIsOpen && currentActiveLimit >= INACTIVITY_LIMIT && currentActiveLimit < FORCE_LOGOUT_LIMIT) {
                setIsOpen(true);
                setCountdown(POPUP_GRACE);
                localStorage.setItem(POPUP_SHOWN_KEY, '1');
            }
            // Force logout if grace period exceeded
            if (currentActiveLimit >= FORCE_LOGOUT_LIMIT) {
                localStorage.setItem(LOGOUT_KEY, '1');
                doLogout();
                return;
            }
            // Decrement countdown in popup, logout if reaches zero
            if (modalIsOpen) {
                setCountdown(prev => {
                    if (prev <= 1) {
                        localStorage.setItem(LOGOUT_KEY, '1');
                        doLogout();
                        return 0;
                    }
                    return prev - 1;
                });
            }
        }, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [modalIsOpen, doLogout, updateLastActive]);

    /**
     * On mount, initialize last active timestamp if not set.
     */
    useEffect(() => {
        if (!localStorage.getItem(AUTOLOGOUT_KEY)) {
            updateLastActive();
        }
    }, [updateLastActive]);

    /**
     * Handler for "Stay logged in" button: closes popup and resets timer.
     */
    const onContinue = () => {
        setIsOpen(false);
        setCountdown(POPUP_GRACE);
        updateLastActive();
    };

    /**
     * Handler for "Logout" button: triggers logout and sets logout flag in storage.
     */
    const onRedirectLoginPage = () => {
        localStorage.setItem(LOGOUT_KEY, '1');
        doLogout();
    };

    /**
     * Handler for "OK" button in signout message: closes signout modal.
     */
    const onShowSignoutMessage = () => {
        setShowSignoutMessage(false);
    };

    // Return state and handlers for use in the component
    return {
        modalIsOpen,
        showSignoutMessage,
        countdown,
        onContinue,
        onRedirectLoginPage,
        onShowSignoutMessage,
    };
}
